import React, { useState, useEffect, useMemo, useRef } from "react";
import Sidebar from "../components/Sidebar"; // Adjust path for your SuperAdmin Sidebar
import { db } from "../firebase/firebase";
import { collection, onSnapshot, doc, updateDoc, deleteDoc, addDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import {
  Store, FileText, Box, Download, FileSpreadsheet, Calendar,
  ArrowLeft, Filter, Package, Users, Eye, BarChart3,
  ShoppingCart, Clock3, CheckCircle2, AlertTriangle,
  IndianRupee, Tag, ChevronLeft, ChevronRight, X, Layers, Edit2, Check, Trash2, Search
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function StoreReport() {
  const [stores, setStores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Real-Time Data Dictionaries for all stores
  const [allSalesMap, setAllSalesMap] = useState({});
  const [allInventoryMap, setAllInventoryMap] = useState({});
  const [allCustomersMap, setAllCustomersMap] = useState({});

  // Layout & Filtering State
  const [activeTab, setActiveTab] = useState("global"); // 'global' or 'stores'
  const [viewingStore, setViewingStore] = useState(null); // Holds store object when detailing
  
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [orderStatusFilter, setOrderStatusFilter] = useState("all");

  // Ledger Specific Filters
  const [ledgerFilters, setLedgerFilters] = useState({
    code: '',
    name: '',
    category: '',
    date: ''
  });

  // Bulk Selection State for Stock Ledger
  const [selectedItemIds, setSelectedItemIds] = useState([]);

  // Export Menu State
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false); // NEW PREVIEW STATE
  const menuRef = useRef(null);

  // View All Modal State
  const [isViewAllOpen, setIsViewAllOpen] = useState(false);
  const [viewAllData, setViewAllData] = useState([]); // Holds data for the modal

  // Inline Editing State for Inventory
  const [editingItem, setEditingItem] = useState(null);
  const [editValues, setEditValues] = useState({ quantity: 0, retailPrice: 0 });

  // Pagination for stock ledger
  const [closingStockPage, setClosingStockPage] = useState(1);
  const recordsPerPage = 9;

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- 1. FETCH REAL-TIME STORES ---
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "stores"), (snapshot) => {
      const storesData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setStores(storesData);
      setIsLoading(false);
    }, (error) => console.error("Error fetching stores:", error));
    return () => unsub();
  }, []);

  // --- 2. FETCH SUB-COLLECTIONS FOR ALL STORES ---
  const storeIdsDep = stores.map(s => s.id).join(',');
  useEffect(() => {
    if (stores.length === 0) return;
    const unsubs = [];
    stores.forEach(store => {
      // Sales
      const unsubSales = onSnapshot(collection(db, "stores", store.id, "sales"), (snap) => {
        setAllSalesMap(prev => ({ 
          ...prev, 
          [store.id]: snap.docs.map(d => ({ ...d.data(), id: d.id, storeName: store.storeName, storeId: store.id }))
        }));
      });
      unsubs.push(unSales => unsubSales());

      // Inventory
      const unsubInventory = onSnapshot(collection(db, "stores", store.id, "inventory"), (snap) => {
        setAllInventoryMap(prev => ({ 
          ...prev, 
          [store.id]: snap.docs.map(d => ({ ...d.data(), id: d.id, storeName: store.storeName, storeId: store.id }))
        }));
      });
      unsubs.push(unsubInventory);

      // Customers
      const unsubCustomers = onSnapshot(collection(db, "stores", store.id, "customers"), (snap) => {
        setAllCustomersMap(prev => ({
          ...prev,
          [store.id]: snap.docs.map(d => ({ ...d.data(), id: d.id }))
        }));
      });
      unsubs.push(unsubCustomers);
    });
    return () => unsubs.forEach(unsub => unsub());
  }, [storeIdsDep]);

  // --- 3. CORE DATA AGGREGATION ENGINE ---
  const { globalData, storeDataMap } = useMemo(() => {
    const storeMap = {};
    const todayStr = new Date().toISOString().split("T")[0];

    // Global Accumulators
    let gSalesSum = 0; let gAdvanceSum = 0; let gCollectedSum = 0; let gDuesSum = 0; let gInvoiceCount = 0;
    let gPendingCount = 0; let gCompletedCount = 0; let gCustomerCount = 0;
    let gTotalStockQty = 0; let gTotalStockVal = 0;
    let gSalesList = []; let gInventoryList = []; let gCustomerList = [];

    stores.forEach(store => {
      let sales = allSalesMap[store.id] || [];
      const inventory = allInventoryMap[store.id] || [];
      const customers = allCustomersMap[store.id] || [];

      // Calendar Filter for Sales
      const filteredSales = sales.filter(s => {
        const orderDate = s.timestamp?.split("T")[0];
        if (!orderDate) return false;
        if (dateFilter.start || dateFilter.end) {
          if (dateFilter.start && orderDate < dateFilter.start) return false;
          if (dateFilter.end && orderDate > dateFilter.end) return false;
          return true;
        }
        return orderDate === todayStr; // Default to today
      });

      // Branch Level Financial Math
      const sSalesSum = filteredSales.reduce((sum, s) => sum + (Number(s.financials?.netPayable || s.financials?.totalAmount || 0)), 0);
      const sCollectedSum = filteredSales.reduce((sum, s) => sum + (Number(s.financials?.amountPaid || s.financials?.paidAmount || s.financials?.advancePayment || 0)), 0);

      const sAdvanceSum = filteredSales.reduce((sum, s) => {
        const adv = Number(s.financials?.advancePayment || s.financials?.advance || 0);
        if (adv > 0) return sum + adv;
        const paid = Number(s.financials?.amountPaid || s.financials?.paidAmount || 0);
        if (s.orderStatus === "Pending" && paid > 0) return sum + paid;
        return sum;
      }, 0);

      const sDuesSum = filteredSales.reduce((sum, s) => {
        const net = Number(s.financials?.netPayable || s.financials?.totalAmount || 0);
        const paid = Number(s.financials?.amountPaid || s.financials?.paidAmount || s.financials?.advancePayment || 0);
        const explicitDue = s.financials?.balanceDue !== undefined ? Number(s.financials.balanceDue) : (s.financials?.dueAmount !== undefined ? Number(s.financials.dueAmount) : null);
        if (explicitDue !== null) return sum + explicitDue;
        return sum + Math.max(0, net - paid);
      }, 0);

      const sInvoiceCount = filteredSales.length;
      const sPendingCount = filteredSales.filter(s => s.orderStatus === "Pending").length;
      const sCompletedCount = filteredSales.filter(s => s.orderStatus === "Completed").length;
      const sCustomerCount = customers.length;
      
      const sStockQty = inventory.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      const sStockVal = inventory.reduce((sum, item) => sum + ((Number(item.quantity) || 0) * (Number(item.retailPrice || item.unitPrice || item.mrp || 0))), 0);

      storeMap[store.id] = {
        storeRef: store,
        sales: filteredSales,
        inventory,
        customers,
        kpi: { 
          sum: sSalesSum, 
          advance: sAdvanceSum, 
          collected: sCollectedSum,
          dues: sDuesSum, 
          invoices: sInvoiceCount, 
          pending: sPendingCount, 
          completed: sCompletedCount, 
          customers: sCustomerCount, 
          stockQty: sStockQty, 
          stockVal: sStockVal 
        }
      };

      // Aggregate into Global
      gSalesSum += sSalesSum;
      gAdvanceSum += sAdvanceSum;
      gCollectedSum += sCollectedSum;
      gDuesSum += sDuesSum;
      gInvoiceCount += sInvoiceCount;
      gPendingCount += sPendingCount;
      gCompletedCount += sCompletedCount;
      gCustomerCount += sCustomerCount;
      gTotalStockQty += sStockQty;
      gTotalStockVal += sStockVal;
      
      gSalesList = [...gSalesList, ...filteredSales];
      gInventoryList = [...gInventoryList, ...inventory];
      gCustomerList = [...gCustomerList, ...customers];
    });

    // Sort global lists
    gSalesList.sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp));
    gInventoryList.sort((a,b) => (b.quantity*(b.retailPrice||0)) - (a.quantity*(a.retailPrice||0)));

    return {
      storeDataMap: storeMap,
      globalData: {
        sales: gSalesList,
        inventory: gInventoryList,
        kpi: { 
          sum: gSalesSum, 
          advance: gAdvanceSum, 
          collected: gCollectedSum,
          dues: gDuesSum, 
          invoices: gInvoiceCount, 
          pending: gPendingCount, 
          completed: gCompletedCount, 
          customers: gCustomerCount, 
          stockQty: gTotalStockQty, 
          stockVal: gTotalStockVal 
        }
      }
    };
  }, [stores, allSalesMap, allInventoryMap, allCustomersMap, dateFilter]);

  // --- 4. VIEW SELECTOR HELPERS ---
  const currentViewData = activeTab === "global" || !viewingStore ? globalData : storeDataMap[viewingStore.id];
  const isGlobal = activeTab === "global" || !viewingStore;

  // Derive Alerts (0 < qty < 5)
  const lowStockAlerts = useMemo(() => {
    return (currentViewData.inventory || []).filter(item => {
      const qty = Number(item.quantity) || 0;
      return qty > 0 && qty < 5;
    }).slice(0, 50);
  }, [currentViewData]);

  // Derive Stock Level Distribution Summary
  const productTypeSummaryTable = useMemo(() => {
    const summary = {};
    (currentViewData.inventory || []).forEach(item => {
      const pType = item.productType || "General";
      const qty = Number(item.quantity) || 0;
      const value = qty * (Number(item.retailPrice || item.unitPrice || item.mrp || 0));
      
      if (!summary[pType]) summary[pType] = { quantity: 0, stockValue: 0 };
      summary[pType].quantity += qty;
      summary[pType].stockValue += value;
    });
    return Object.entries(summary).map(([type, data]) => ({ type, ...data })).sort((a,b) => b.stockValue - a.stockValue);
  }, [currentViewData]);

  // Derive Orders Ledger (Filtered by Status)
  const filteredOrdersTable = useMemo(() => {
    let ordersMap = (currentViewData.sales || []).map(s => ({
      id: s.invoiceNo || s.id?.slice(-6).toUpperCase(),
      store: s.storeName || "Network",
      customer: s.customer?.name || "Walk-in Client",
      amount: s.financials?.netPayable || 0,
      status: s.orderStatus || "Completed",
      type: s.items?.[0]?.productName ? `${s.items[0].productName} ${s.items.length > 1 ? `+${s.items.length - 1}` : ""}` : "Optical Items",
      date: s.timestamp?.split("T")[0]
    }));

    if (orderStatusFilter !== "all") {
      ordersMap = ordersMap.filter(o => o.status.toLowerCase() === orderStatusFilter);
    }
    return ordersMap.slice(0, 15);
  }, [currentViewData, orderStatusFilter]);

  // Paginated & Filtered Inventory List for Physical Ledger
  const activeInventoryOnly = useMemo(() => {
    return (currentViewData.inventory || []).filter(item => (Number(item.quantity) || 0) > 0);
  }, [currentViewData]);

  const uniqueCategories = useMemo(() => {
    return [...new Set(activeInventoryOnly.map(item => item.productType || item.category || "General"))];
  }, [activeInventoryOnly]);

  const filteredInventoryData = useMemo(() => {
    let data = activeInventoryOnly;
    if (ledgerFilters.code) {
      data = data.filter(item => (item.productCode || item.sku || '').toLowerCase().includes(ledgerFilters.code.toLowerCase()));
    }
    if (ledgerFilters.name) {
      data = data.filter(item => (item.productName || '').toLowerCase().includes(ledgerFilters.name.toLowerCase()));
    }
    if (ledgerFilters.category) {
      data = data.filter(item => (item.productType || item.category || 'General').toLowerCase() === ledgerFilters.category.toLowerCase());
    }
    if (ledgerFilters.date) {
      data = data.filter(item => (item.timestamp || item.dateAdded || '').includes(ledgerFilters.date));
    }
    return data;
  }, [activeInventoryOnly, ledgerFilters]);
  
  const paginatedInventoryData = useMemo(() => {
    const offset = (closingStockPage - 1) * recordsPerPage;
    return filteredInventoryData.slice(offset, offset + recordsPerPage);
  }, [filteredInventoryData, closingStockPage]);
  const maxClosingStockPages = Math.ceil(filteredInventoryData.length / recordsPerPage) || 1;

  // --- MULTI-SELECT HANDLERS ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = paginatedInventoryData.map(item => item.id);
      setSelectedItemIds(allIds);
    } else {
      setSelectedItemIds([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItemIds(prev =>
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    );
  };

  // --- BULK DELETE INVENTORY HANDLER ---
  const handleBulkDeleteInventory = async () => {
    if (selectedItemIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedItemIds.length} selected item(s)? This will remove them from both inventory and purchase records.`)) return;

    try {
      const itemsToDelete = (currentViewData.inventory || []).filter(item => selectedItemIds.includes(item.id));
      
      await Promise.all(itemsToDelete.map(async (item) => {
        const targetStoreId = item.storeId || viewingStore?.id;
        if (!targetStoreId) return;

        // 1. Log deleted item
        await addDoc(collection(db, "super_admin_deleted_inventory"), {
          ...item,
          deletedStoreId: targetStoreId,
          deletedAt: new Date().toISOString()
        });

        // 2. Delete inventory doc
        await deleteDoc(doc(db, "stores", targetStoreId, "inventory", item.id));

        // 3. Delete purchase doc
        await deleteDoc(doc(db, "stores", targetStoreId, "purchase", item.id));
      }));

      setSelectedItemIds([]);
      alert("Selected items successfully deleted and logged.");
    } catch (error) {
      console.error("Error bulk deleting items:", error);
      alert("Failed to delete selected items completely.");
    }
  };

  // --- 5. INLINE EDITING LOGIC (STORE VIEW ONLY) ---
  const startEdit = (item) => {
    setEditingItem(item.id);
    setEditValues({
      quantity: Number(item.quantity) || 0,
      retailPrice: Number(item.retailPrice || item.mrp || item.unitPrice) || 0
    });
  };
  const cancelEdit = () => setEditingItem(null);
  
  const saveEdit = async (storeId, itemId) => {
    try {
      const itemRef = doc(db, "stores", storeId, "inventory", itemId);
      await updateDoc(itemRef, {
        quantity: Number(editValues.quantity),
        retailPrice: Number(editValues.retailPrice)
      });
      setEditingItem(null);
    } catch (error) {
      console.error("Error updating inventory:", error);
      alert("Failed to update inventory.");
    }
  };

  // --- SINGLE INVENTORY DELETION LOGIC ---
  const handleDeleteInventory = async (storeId, item) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${item.productName}"? This will remove it from both inventory and purchase records.`)) return;

    try {
      await addDoc(collection(db, "super_admin_deleted_inventory"), {
        ...item,
        deletedStoreId: storeId,
        deletedAt: new Date().toISOString()
      });

      await deleteDoc(doc(db, "stores", storeId, "inventory", item.id));
      await deleteDoc(doc(db, "stores", storeId, "purchase", item.id));

      alert("Item successfully deleted and logged.");
    } catch (error) {
      console.error("Error deleting item:", error);
      alert("Failed to delete item completely.");
    }
  };

  // --- 6. PROFESSIONAL PDF & EXCEL EXPORT ENGINE ---
  const executeExport = (format) => {
    const reportTitleContext = isGlobal ? "GLOBAL NETWORK METRICS" : `BRANCH PROFILE: ${viewingStore.storeName}`;
    const dateStr = dateFilter.start || dateFilter.end ? `${dateFilter.start || 'Start'} to ${dateFilter.end || 'Now'}` : 'Today\'s Data Feed';

    if (format === "excel") {
      let csv = "data:text/csv;charset=utf-8,";
      csv += `--- SUPER ADMIN BUSINESS REPORT: ${reportTitleContext} ---\n`;
      csv += `Filter Scope: ${dateStr}\n\n`;
      
      csv += "--- KPI SUMMARY ---\n";
      csv += `Revenue,${currentViewData.kpi.sum}\nPending Invoices,${currentViewData.kpi.pending}\nCompleted Invoices,${currentViewData.kpi.completed}\nTotal Customers,${currentViewData.kpi.customers}\nTotal Stock Qty,${currentViewData.kpi.stockQty}\nTotal Stock Value,${currentViewData.kpi.stockVal}\n\n`;

      csv += "--- SALES LEDGER ---\n";
      csv += "Invoice ID,Store,Date,Customer,Amount (INR),Status\n";
      currentViewData.sales.forEach(s => {
        csv += `${s.invoiceNo || 'N/A'},"${s.storeName || ''}",${s.timestamp?.split("T")[0]},"${s.customer?.name || 'Walk-In'}",${s.financials?.netPayable || 0},${s.orderStatus || 'Completed'}\n`;
      });

      csv += "\n--- INVENTORY LEDGER ---\n";
      csv += "SKU,Product Name,Store,Category,Qty,Unit Price,Total Value\n";
      currentViewData.inventory.forEach(i => {
        const qty = Number(i.quantity) || 0;
        const price = Number(i.retailPrice || i.mrp || 0);
        csv += `"${i.productCode || i.sku || ''}","${i.productName || ''}","${i.storeName || ''}","${i.productType || ''}",${qty},${price},${qty * price}\n`;
      });

      const encodedUri = encodeURI(csv);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `ERP_Report_${isGlobal ? 'Global' : viewingStore.storeName.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (format === "pdf") {
      const docPDF = new jsPDF("p", "mm", "a4");
      const timestampString = new Date().toLocaleString();

      const drawHeaderAndFooter = (pdfInstance, titleString, pageNum) => {
        pdfInstance.setFillColor(30, 41, 59); // Slate 800
        pdfInstance.rect(0, 0, 210, 24, "F");
        pdfInstance.setFont("helvetica", "bold");
        pdfInstance.setFontSize(14);
        pdfInstance.setTextColor(255, 255, 255);
        pdfInstance.text(`SUPER ADMIN REPORT - ${titleString.toUpperCase()}`, 14, 11);
        pdfInstance.setFont("helvetica", "normal");
        pdfInstance.setFontSize(8);
        pdfInstance.setTextColor(148, 163, 184);
        pdfInstance.text(`Context: ${reportTitleContext} | Filter: ${dateStr}`, 14, 18);
        pdfInstance.setTextColor(255, 255, 255);
        pdfInstance.text(`Generated: ${timestampString}`, 130, 11);
        pdfInstance.setFillColor(37, 99, 235); // Blue 600 line
        pdfInstance.rect(0, 24, 210, 1.5, "F");
        pdfInstance.setFontSize(8);
        pdfInstance.setTextColor(100, 116, 139);
        pdfInstance.text(`Page ${pageNum}`, 190, 288);
      };

      // PAGE 1: Overview Summary Stats
      drawHeaderAndFooter(docPDF, "Executive Summary", 1);
      docPDF.setFont("helvetica", "bold");
      docPDF.setFontSize(11);
      docPDF.setTextColor(30, 41, 59);
      docPDF.text("1. CORE LOGISTICS PERFORMANCE METRICS", 14, 36);

      autoTable(docPDF, {
        startY: 40,
        head: [["Performance Dynamic Property Category", "Computed Architecture State Matrix Data"]],
        body: [
          ["Filtered Revenue Net Yield", `INR ${currentViewData.kpi.sum.toLocaleString()}`],
          ["Completed Log Order Invoices", String(currentViewData.kpi.completed)],
          ["Pending State Pipeline Invoices", String(currentViewData.kpi.pending)],
          ["Total CRM Registered Profiles", String(currentViewData.kpi.customers)],
          ["Cumulative Physical Item Inventory Units", `${currentViewData.kpi.stockQty.toLocaleString()} Units`],
          ["Net Total Asset Stock Valuation (Retail)", `INR ${currentViewData.kpi.stockVal.toLocaleString()}`]
        ],
        theme: "grid", headStyles: { fillColor: [30, 41, 59] }
      });

      // Product Type Valuation Summary Matrix
      let currentY = docPDF.lastAutoTable.finalY + 12;
      docPDF.text("2. PRODUCT MODULE DISTRIBUTION MAP", 14, currentY);
      autoTable(docPDF, {
        startY: currentY + 4,
        head: [["Product Type", "On-Hand Units", "Total Valuation (Retail)"]],
        body: productTypeSummaryTable.map(item => [item.type, `${item.quantity.toLocaleString()} Units`, `INR ${item.stockValue.toLocaleString()}`]),
        theme: "striped", headStyles: { fillColor: [37, 99, 235] }
      });

      // PAGE 2: Exceptions & Recent Sales
      docPDF.addPage();
      drawHeaderAndFooter(docPDF, "Ledgers & Exceptions", 2);
      
      docPDF.setFont("helvetica", "bold");
      docPDF.setFontSize(11);
      docPDF.setTextColor(30, 41, 59);
      docPDF.text("3. EXCEPTION WATCH: LOW-STOCK ALERTS", 14, 36);
      
      autoTable(docPDF, {
        startY: 40,
        head: [["SKU / Code", "Product Name", "Store Node", "Status Qty"]],
        body: lowStockAlerts.length > 0 ? lowStockAlerts.map(item => [item.productCode || item.sku || "N/A", item.productName || "Product", item.storeName || "Branch", `${item.quantity} LEFT`]) : [["-", "Zero Alerts Detected", "-", "-"]],
        theme: "grid", headStyles: { fillColor: [239, 68, 68] }
      });

      currentY = docPDF.lastAutoTable.finalY + 12;
      docPDF.text("4. RECENT TRANSACTION LEDGER (Top 50)", 14, currentY);
      autoTable(docPDF, {
        startY: currentY + 4,
        head: [["Invoice", "Store", "Date", "Customer", "Amount", "Status"]],
        body: currentViewData.sales.slice(0, 50).map(s => [s.invoiceNo || "N/A", s.storeName || "N/A", s.timestamp?.split("T")[0] || "-", s.customer?.name || "Walk-In", `INR ${s.financials?.netPayable || 0}`, s.orderStatus || "Completed"]),
        theme: "striped", headStyles: { fillColor: [16, 185, 129] }
      });

      docPDF.save(`SuperAdmin_Audit_${isGlobal ? 'GLOBAL' : viewingStore.storeName.replace(/\s+/g, '_')}.pdf`);
    }
    setShowExportMenu(false);
  };

  const handleOpenViewAll = () => {
    setViewAllData(filteredInventoryData);
    setIsViewAllOpen(true);
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-blue-200">
      <Sidebar />
      <div className="flex-1 overflow-auto flex flex-col overflow-x-hidden">

        <div className="p-4 lg:p-8 space-y-6">
          
          {/* --- TOP GLOBAL HEADER & CONTROLS --- */}
          <motion.div initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
            <div>
              <h1 className="text-2xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                <BarChart3 size={24} className="text-blue-600" /> Super Admin Operations Report
              </h1>
              <p className="text-sm text-slate-500 font-medium mt-1">Comprehensive Multi-Branch Analytics & Physical Ledgers</p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full xl:w-auto">
              {/* Calendar Constraints */}
              <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-2xl border border-slate-200 w-full sm:w-auto shadow-inner">
                <Calendar className="text-blue-500 ml-2" size={18} />
                <input type="date" className="bg-transparent text-sm text-slate-700 outline-none font-bold" value={dateFilter.start} onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})} />
                <span className="text-slate-400 text-[10px] font-black uppercase">TO</span>
                <input type="date" className="bg-transparent text-sm text-slate-700 outline-none font-bold pr-2" value={dateFilter.end} onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})} />
              </div>

              {/* Document Export Node */}
              <div className="relative w-full sm:w-auto" ref={menuRef}>
                <button onClick={() => setShowExportMenu(!showExportMenu)} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-slate-900 text-white hover:bg-slate-800 rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-md">
                  <Download size={16} className="text-blue-400" /> Export
                </button>
                <AnimatePresence>
                  {showExportMenu && (
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }} className="absolute right-0 top-full mt-2 bg-white shadow-xl rounded-2xl p-2 w-56 z-50 border border-slate-100">
                      <button onClick={() => { setShowPreview(true); setShowExportMenu(false); }} className="flex items-center gap-3 px-4 py-3 w-full hover:bg-slate-50 rounded-xl text-xs font-bold text-slate-700 transition-colors">
                        <Eye size={16} className="text-blue-500"/> Preview
                      </button>
                      <div className="border-t border-slate-100 my-1"></div>
                      <button onClick={() => executeExport("excel")} className="flex items-center gap-3 px-4 py-3 w-full hover:bg-slate-50 rounded-xl text-xs font-bold text-slate-700 transition-colors">
                        <FileSpreadsheet size={16} className="text-emerald-500"/> Export Excel
                      </button>
                      <button onClick={() => executeExport("pdf")} className="flex items-center gap-3 px-4 py-3 w-full hover:bg-slate-50 rounded-xl text-xs font-bold text-slate-700 transition-colors">
                        <FileText size={16} className="text-rose-500"/> Export PDF
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* --- TAB CONTROL INTERFACE --- */}
          <div className="flex bg-slate-200/50 p-1 rounded-2xl w-full max-w-md shadow-inner">
            <button 
              onClick={() => { setActiveTab("global"); setViewingStore(null); setSelectedItemIds([]); }}
              className={`flex-1 py-3 text-sm font-black uppercase tracking-wider rounded-xl transition-all ${activeTab === "global" ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Global Network View
            </button>
            <button 
              onClick={() => { setActiveTab("stores"); setSelectedItemIds([]); }}
              className={`flex-1 py-3 text-sm font-black uppercase tracking-wider rounded-xl transition-all ${activeTab === "stores" ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Branch Deep-Dive
            </button>
          </div>

          {/* --- MAIN DASHBOARD VIEWPORT --- */}
          <AnimatePresence mode="wait">
            
            {/* If in Stores tab and NO store is selected -> Show Store Grid */}
            {activeTab === "stores" && !viewingStore ? (
              <motion.div key="grid" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {stores.map((store) => {
                  const mKPI = storeDataMap[store.id]?.kpi || { sum: 0 };
                  return (
                    <div key={store.id} onClick={() => setViewingStore(store)} className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm hover:border-blue-400 hover:shadow-md transition-all cursor-pointer flex justify-between items-center group">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-50 text-blue-600 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors">
                          <Store size={22} />
                        </div>
                        <div>
                          <h3 className="font-black text-slate-800 text-lg group-hover:text-blue-600 transition-colors">{store.storeName || "Unnamed Branch"}</h3>
                          <p className="text-slate-400 font-mono text-[11px] uppercase tracking-wider mt-0.5">ID: {store.storeId || store.id}</p>
                        </div>
                      </div>
                      <div className="text-right flex flex-col items-end">
                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Filtered Yield</span>
                        <span className="text-base font-black text-emerald-600 font-mono">₹{mKPI.sum.toLocaleString()}</span>
                      </div>
                    </div>
                  )
                })}
              </motion.div>
            ) : (
              
              /* RENDER THE UNIFIED DASHBOARD LAYOUT */
              <motion.div key="dashboard" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-6">
                
                {/* SUB-HEADER IF VIEWING A SPECIFIC STORE */}
                {!isGlobal && viewingStore && (
                  <div className="bg-slate-900 text-white p-5 rounded-2xl shadow-md space-y-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <button onClick={() => { setViewingStore(null); setSelectedItemIds([]); }} className="p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"><ArrowLeft size={18} /></button>
                        <div>
                          <h2 className="text-lg font-black uppercase tracking-wider">Branch Details: {viewingStore.storeName}</h2>
                          <span className="text-slate-400 text-[10px] font-mono tracking-widest uppercase">ID: {viewingStore.storeId || viewingStore.id}</span>
                        </div>
                      </div>
                      <div className="text-[10px] font-bold text-slate-400 bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700">
                        Showing data for: {dateFilter.start || dateFilter.end ? `${dateFilter.start || '...'} to ${dateFilter.end || '...'}` : 'Today'}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 pt-4 border-t border-slate-800">
                      <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                        <p className="text-[10px] uppercase font-bold text-emerald-400 tracking-wider mb-1">Sales Report</p>
                        <h4 className="text-xl font-black font-mono">₹{currentViewData.kpi.sum.toLocaleString()}</h4>
                      </div>
                      <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                        <p className="text-[10px] uppercase font-bold text-blue-400 tracking-wider mb-1">Advance Received</p>
                        <h4 className="text-xl font-black font-mono">₹{currentViewData.kpi.advance.toLocaleString()}</h4>
                      </div>
                      <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                        <p className="text-[10px] uppercase font-bold text-purple-400 tracking-wider mb-1">Total Invoices</p>
                        <h4 className="text-xl font-black font-mono">{currentViewData.kpi.invoices}</h4>
                      </div>
                      <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                        <p className="text-[10px] uppercase font-bold text-rose-400 tracking-wider mb-1">Dues Outstanding</p>
                        <h4 className="text-xl font-black font-mono">₹{currentViewData.kpi.dues.toLocaleString()}</h4>
                      </div>
                      <div className="bg-slate-800/50 p-4 rounded-xl border border-slate-700/50">
                        <p className="text-[10px] uppercase font-bold text-emerald-400 tracking-wider mb-1">Total Payment Ref</p>
                        <h4 className="text-xl font-black font-mono">₹{currentViewData.kpi.collected.toLocaleString()}</h4>
                      </div>
                    </div>
                  </div>
                )}

                {/* KPI METRICS STRIP GRID */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                  {[
                    { title: dateFilter.start || dateFilter.end ? "Filtered Revenue" : "Today's Revenue", value: `₹${currentViewData.kpi.sum.toLocaleString()}`, icon: IndianRupee, bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600", iconBg: "bg-emerald-100" },
                    { title: "Pending Invoices", value: currentViewData.kpi.pending, icon: Clock3, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-600", iconBg: "bg-amber-100" },
                    { title: "Completed Invoices", value: currentViewData.kpi.completed, icon: CheckCircle2, bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-600", iconBg: "bg-blue-100" },
                    { title: "Total CRM Customers", value: currentViewData.kpi.customers, icon: Users, bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-600", iconBg: "bg-purple-100" }
                  ].map((item, i) => (
                    <motion.div key={i} whileHover={{ scale: 1.02, y: -2 }} className={`${item.bg} border ${item.border} rounded-2xl p-6 shadow-sm flex flex-col justify-between h-36 relative overflow-hidden`}>
                      <div className="flex justify-between items-start font-bold">
                        <span className={`text-xs uppercase tracking-wider ${item.text} opacity-80`}>{item.title}</span>
                        <div className={`p-2 rounded-xl ${item.iconBg} ${item.text}`}><item.icon size={16} /></div>
                      </div>
                      <h2 className={`text-3xl font-mono font-black tracking-tight ${item.text}`}>{item.value}</h2>
                    </motion.div>
                  ))}
                </div>

                {/* ALERTS & SUMMARY MAPS ROW */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* CRITICAL STOCK ALERTS */}
                  <div className="lg:col-span-1 bg-rose-50 border border-rose-200 rounded-3xl p-6 shadow-sm flex flex-col">
                    <h3 className="text-sm font-black uppercase tracking-wider text-rose-600 flex items-center gap-2 mb-4">
                      <AlertTriangle size={18} /> Critical Stock Alerts
                    </h3>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar max-h-[320px]">
                      {lowStockAlerts.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-rose-400/70 text-center space-y-2 py-8">
                          <CheckCircle2 size={32} />
                          <p className="text-xs font-bold uppercase tracking-wider">All Stock Optimal</p>
                        </div>
                      ) : (
                        lowStockAlerts.map((item, idx) => (
                          <div key={`${item.id}-${idx}`} className="bg-white p-3 rounded-xl border border-rose-100 shadow-sm flex justify-between items-center">
                            <div className="flex flex-col min-w-0 flex-1 pr-2">
                              <span className="text-xs font-bold text-slate-700 truncate">{item.productName || "Optical Item"}</span>
                              <div className="flex items-center gap-1 mt-0.5">
                                <span className="text-[9px] font-mono font-bold text-slate-400 truncate">{item.productCode || item.sku || "No SKU"}</span>
                                {isGlobal && <span className="text-[8px] bg-slate-100 text-slate-500 px-1 rounded truncate ml-1">{item.storeName}</span>}
                              </div>
                            </div>
                            <span className="text-[10px] font-black font-mono bg-rose-100 text-rose-600 px-2 py-1 rounded-md shrink-0">
                              {item.quantity} LEFT
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* PRODUCT TYPE DISTRIBUTION */}
                  <div className="lg:col-span-2 bg-white border border-slate-200 rounded-3xl p-6 shadow-sm flex flex-col overflow-hidden">
                    <h3 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2 mb-3">
                      <Package size={18} className="text-blue-500" /> Stock Level Distribution Summary
                    </h3>
                    <div className="overflow-x-auto flex-1 text-[11px] font-medium custom-scrollbar max-h-[320px]">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[9px]">
                            <th className="p-3 pl-4">Product Type Identifier</th>
                            <th className="p-3 text-center">Total Quantity</th>
                            <th className="p-3 text-right pr-4">Total Stock Value (Retail)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-slate-700">
                          {productTypeSummaryTable.map((row) => (
                            <tr key={row.type} className="hover:bg-slate-50/70 transition-colors">
                              <td className="p-3 pl-4 font-bold text-slate-800 uppercase">{row.type}</td>
                              <td className="p-3 text-center font-mono font-black text-blue-600">{row.quantity.toLocaleString()}</td>
                              <td className="p-3 text-right font-mono font-black text-emerald-600 pr-4">₹{row.stockValue.toLocaleString()}</td>
                            </tr>
                          ))}
                          {productTypeSummaryTable.length === 0 && (
                            <tr><td colSpan={3} className="p-6 text-center text-slate-400 font-bold uppercase">No Storage Framework Found Mapped</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* --- FILTERS FOR COMPREHENSIVE PHYSICAL STOCK LEDGER --- */}
                <div className="bg-slate-100/60 p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col lg:flex-row items-center gap-4 mt-6">
                  <div className="flex items-center gap-2 text-slate-500 w-full lg:w-auto font-black uppercase text-xs tracking-wider border-r border-slate-200 pr-4">
                    <Filter size={16} className="text-blue-500" /> Ledger Filters
                  </div>
                  <div className="flex flex-1 w-full grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                      <input type="text" placeholder="Search Code/SKU..." value={ledgerFilters.code} onChange={(e) => { setLedgerFilters({...ledgerFilters, code: e.target.value}); setClosingStockPage(1); }} className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-blue-500 transition-colors" />
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                      <input type="text" placeholder="Search Product Name..." value={ledgerFilters.name} onChange={(e) => { setLedgerFilters({...ledgerFilters, name: e.target.value}); setClosingStockPage(1); }} className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-blue-500 transition-colors" />
                    </div>
                    <div className="relative">
                      <Layers className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                      <select value={ledgerFilters.category} onChange={(e) => { setLedgerFilters({...ledgerFilters, category: e.target.value}); setClosingStockPage(1); }} className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-blue-500 transition-colors appearance-none">
                        <option value="">All Categories</option>
                        {uniqueCategories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div className="relative">
                      <Calendar className="absolute left-2.5 top-2.5 text-slate-400" size={14} />
                      <input type="date" value={ledgerFilters.date} onChange={(e) => { setLedgerFilters({...ledgerFilters, date: e.target.value}); setClosingStockPage(1); }} className="w-full pl-8 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:border-blue-500 transition-colors" />
                    </div>
                  </div>
                </div>

                {/* COMPREHENSIVE PHYSICAL STOCK LEDGER CARD */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                    <div>
                      <h2 className="text-sm font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                        <Box size={18} className="text-blue-500" /> Comprehensive Physical Stock Ledger
                      </h2>
                      <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                        Real-time valuation for {isGlobal ? 'entire network' : viewingStore.storeName}
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2">
                      {/* BULK DELETE ACTION BUTTON */}
                      {selectedItemIds.length > 0 && (
                        <button 
                          onClick={handleBulkDeleteInventory}
                          className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-sm flex items-center gap-1.5 animate-in fade-in zoom-in-95 duration-150"
                        >
                          <Trash2 size={14} /> Delete Selected ({selectedItemIds.length})
                        </button>
                      )}

                      <div className="text-[11px] font-black text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 flex items-center gap-3">
                        <div>TTL BAL: <span className="text-blue-600 font-mono font-black">{currentViewData.kpi.stockQty.toLocaleString()} Units</span></div>
                        <div className="w-px h-3 bg-slate-300"></div>
                        <div>TTL VAL: <span className="text-emerald-600 font-mono font-black">₹{currentViewData.kpi.stockVal.toLocaleString()}</span></div>
                      </div>
                      <button 
                        onClick={handleOpenViewAll}
                        className="px-4 py-2 bg-blue-50 border border-blue-200 hover:bg-blue-600 text-blue-600 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-sm flex items-center gap-1"
                      >
                        <Eye size={14} /> View All
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto text-[11px] font-medium custom-scrollbar">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 font-bold text-slate-500 uppercase tracking-wider text-[10px]">
                          {/* MULTI-SELECT CHECKBOX COLUMN HEADER */}
                          <th className="p-3 pl-4 w-10 text-center">
                            <input 
                              type="checkbox" 
                              onChange={handleSelectAll} 
                              checked={paginatedInventoryData.length > 0 && paginatedInventoryData.every(item => selectedItemIds.includes(item.id))}
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                          </th>
                          <th className="p-3">Sr. No.</th>
                          <th className="p-3">SKU / Code</th>
                          <th className="p-3">Product Name</th>
                          {isGlobal && <th className="p-3 text-blue-600">Origin Store</th>}
                          <th className="p-3">Product Type</th>
                          <th className="p-3 text-center">Qty Balance</th>
                          <th className="p-3 text-right">Unit Price</th>
                          <th className="p-3 text-right pr-4">Stock Valuation</th>
                          {!isGlobal && <th className="p-3 text-center">Action</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-700">
                        {paginatedInventoryData.length === 0 ? (
                          <tr><td colSpan={isGlobal ? 10 : 9} className="p-8 text-center text-slate-400 font-medium">Empty Frame State: No active stock found based on your filters.</td></tr>
                        ) : (
                          paginatedInventoryData.map((item, idx) => {
                            const isEditing = !isGlobal && editingItem === item.id;
                            const currentQty = isEditing ? Number(editValues.quantity) : (Number(item.quantity) || 0);
                            const uPrice = isEditing ? Number(editValues.retailPrice) : (Number(item.retailPrice || item.unitPrice || item.purchasePrice || 0));
                            const stockValuation = currentQty * uPrice;
                            const serialNumber = (closingStockPage - 1) * recordsPerPage + idx + 1;
                            const isSelected = selectedItemIds.includes(item.id);

                            return (
                              <tr key={`${item.id}-${idx}`} className={`transition-colors ${isSelected ? 'bg-blue-50/50' : 'hover:bg-slate-50/60'}`}>
                                {/* ROW CHECKBOX */}
                                <td className="p-3 pl-4 text-center">
                                  <input 
                                    type="checkbox" 
                                    checked={isSelected}
                                    onChange={() => handleSelectItem(item.id)}
                                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                </td>
                                <td className="p-3 font-mono font-bold text-slate-400">{serialNumber}</td>
                                <td className="p-3 font-mono font-bold text-slate-500">{item.productCode || item.sku || "N/A"}</td>
                                <td className="p-3 font-bold text-slate-900">{item.productName || "Unnamed Model"}</td>
                                {isGlobal && <td className="p-3 font-bold text-blue-600">{item.storeName}</td>}
                                <td className="p-3 text-slate-500 uppercase text-[10px] font-bold">{item.productType || "Standard"}</td>
                                
                                <td className="p-3 text-center font-mono font-black">
                                  {isEditing ? (
                                    <input type="number" value={editValues.quantity} onChange={e => setEditValues({...editValues, quantity: e.target.value})} className="w-16 p-1 text-center border border-blue-300 rounded outline-none shadow-sm font-mono text-slate-700" />
                                  ) : (
                                    <span className={currentQty === 0 ? "text-slate-300 line-through" : currentQty < 5 ? "text-rose-600 bg-rose-50 px-2 py-0.5 rounded" : "text-blue-600"}>
                                      {currentQty}
                                    </span>
                                  )}
                                </td>
                                
                                <td className="p-3 text-right font-mono font-bold text-slate-600">
                                  {isEditing ? (
                                    <div className="flex items-center justify-end gap-1">
                                      <span className="text-slate-400">₹</span>
                                      <input type="number" value={editValues.retailPrice} onChange={e => setEditValues({...editValues, retailPrice: e.target.value})} className="w-20 p-1 text-right border border-blue-300 rounded outline-none shadow-sm font-mono text-slate-700" />
                                    </div>
                                  ) : `₹${uPrice.toLocaleString()}`}
                                </td>

                                <td className="p-3 text-right font-mono font-black text-emerald-600 pr-4">₹{stockValuation.toLocaleString()}</td>
                                
                                {!isGlobal && (
                                  <td className="p-3 text-center">
                                    {isEditing ? (
                                      <div className="flex justify-center gap-1">
                                        <button onClick={() => saveEdit(item.storeId, item.id)} className="p-1 bg-emerald-100 text-emerald-600 rounded hover:bg-emerald-200"><Check size={14}/></button>
                                        <button onClick={cancelEdit} className="p-1 bg-rose-100 text-rose-600 rounded hover:bg-rose-200"><X size={14}/></button>
                                      </div>
                                    ) : (
                                      <div className="flex justify-center gap-1">
                                        <button onClick={() => startEdit(item)} className="p-1.5 bg-slate-100 text-slate-500 rounded hover:bg-blue-100 hover:text-blue-600 transition-colors"><Edit2 size={14}/></button>
                                        <button onClick={() => handleDeleteInventory(item.storeId, item)} className="p-1.5 bg-rose-50 text-rose-500 rounded hover:bg-rose-100 hover:text-rose-600 transition-colors"><Trash2 size={14}/></button>
                                      </div>
                                    )}
                                  </td>
                                )}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* PAGINATION CONTROLS */}
                  {filteredInventoryData.length > recordsPerPage && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-100 text-[11px] font-bold text-slate-500">
                      <div>Page Indices: <span className="text-slate-800 font-black font-mono">{closingStockPage}</span> / <span className="text-slate-800 font-black font-mono">{maxClosingStockPages}</span> generated maps</div>
                      <div className="flex items-center gap-1">
                        <button disabled={closingStockPage === 1} onClick={() => setClosingStockPage(prev => Math.max(prev - 1, 1))} className="p-1.5 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-40"> <ChevronLeft size={15} /> </button>
                        {Array.from({ length: Math.min(maxClosingStockPages, 5) }, (_, i) => {
                           const pageNum = closingStockPage > 3 ? closingStockPage - 2 + i : i + 1;
                           if(pageNum > maxClosingStockPages) return null;
                           return (
                             <button key={pageNum} onClick={() => setClosingStockPage(pageNum)} className={`px-2.5 py-1 font-mono rounded-md transition-all ${closingStockPage === pageNum ? 'bg-blue-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                               {pageNum}
                             </button>
                           )
                        })}
                        <button disabled={closingStockPage === maxClosingStockPages} onClick={() => setClosingStockPage(prev => Math.min(prev + 1, maxClosingStockPages))} className="p-1.5 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-40"> <ChevronRight size={15} /> </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* FILTERED ORDERS MANAGEMENT LEDGER */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h2 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2">
                      <ShoppingCart size={18} className="text-blue-500" /> Filtered Orders Ledger
                    </h2>
                    <div className="flex bg-slate-100 border border-slate-200 p-1 rounded-xl text-[10px] font-bold">
                      {["all", "pending", "completed"].map(f => (
                        <button key={f} onClick={() => setOrderStatusFilter(f)} className={`px-4 py-1.5 rounded-lg capitalize transition-all ${orderStatusFilter === f ? "bg-white text-blue-600 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}>
                          {f}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="overflow-x-auto text-[11px] font-medium border border-slate-100 rounded-2xl custom-scrollbar">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 font-bold text-slate-500 uppercase tracking-wider">
                          <th className="p-4 pl-6">Invoice ID</th>
                          {isGlobal && <th className="p-4 text-blue-600">Origin Store</th>}
                          <th className="p-4">Customer Name</th>
                          <th className="p-4">Date</th>
                          <th className="p-4 text-right">Net Amount</th>
                          <th className="p-4 pr-6 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-700">
                        {filteredOrdersTable.length === 0 ? (
                          <tr><td colSpan={isGlobal ? 6 : 5} className="p-8 text-center text-slate-500 font-medium">No orders matched the current dashboard filters.</td></tr>
                        ) : (
                          filteredOrdersTable.map((o, idx) => (
                            <tr key={`${o.id}-${idx}`} className="hover:bg-slate-50/80 transition-colors">
                              <td className="p-4 pl-6 font-mono font-bold text-blue-600 text-xs">#{o.id}</td>
                              {isGlobal && <td className="p-4 font-bold text-slate-800 text-xs">{o.store}</td>}
                              <td className="p-4 font-bold text-slate-800 text-xs">{o.customer}</td>
                              <td className="p-4 text-slate-500">{o.date || "-"}</td>
                              <td className="p-4 font-mono font-bold text-slate-800 text-xs text-right">₹{o.amount.toLocaleString()}</td>
                              <td className="p-4 pr-6 font-black uppercase text-[10px] tracking-wide text-center">
                                <span className={`px-2 py-1 rounded-md ${o.status === "Completed" ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-amber-50 text-amber-600 border border-amber-200"}`}>
                                  {o.status}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* VIEW ALL POPUP RECONCILED SEGMENT MODULE */}
      <AnimatePresence>
        {isViewAllOpen && (
          <div className="fixed inset-0 z-[60] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 font-sans">
            <motion.div 
              initial={{ scale: 0.98, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.98, opacity: 0 }}
              className="bg-white w-full max-w-5xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[85vh]"
            >
              <div className="bg-slate-900 p-5 text-white flex items-center justify-between shrink-0">
                <div>
                  <h3 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
                    <Package size={16} className="text-blue-400" /> Full Operational Active Inventory Matrix
                  </h3>
                  <p className="text-[10px] text-slate-400 mt-0.5 uppercase tracking-wider font-bold">Trace Balance Instances Total Logged: {viewAllData.length} lines entries</p>
                </div>
                <button onClick={() => setIsViewAllOpen(false)} className="p-1 hover:bg-rose-600 rounded-lg text-slate-400 hover:text-white transition-all">
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto text-[11px] custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-100 uppercase tracking-wider font-bold text-slate-600 text-[9px] sticky top-0 border-b border-slate-200 z-10">
                    <tr>
                      <th className="p-3 pl-5">SKU / Code ID</th>
                      <th className="p-3">Designation Label</th>
                      {isGlobal && <th className="p-3 text-blue-600">Store Branch</th>}
                      <th className="p-3">Model Type</th>
                      <th className="p-3">Category</th>
                      <th className="p-3 text-center">Quantity Balance</th>
                      <th className="p-3 text-right pr-5">Retail Price Valuation</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                    {viewAllData.map((item, idx) => (
                      <tr key={`${item.id}-${idx}`} className="hover:bg-slate-50 transition-colors">
                        <td className="p-3 pl-5 font-mono text-slate-500 font-bold">{item.productCode || item.sku || "N/A"}</td>
                        <td className="p-3 font-black text-slate-800">{item.productName || "Product Reference Item"}</td>
                        {isGlobal && <td className="p-3 font-bold text-blue-600">{item.storeName}</td>}
                        <td className="p-3 uppercase text-[9px] font-bold text-slate-400">{item.productType || "Standard"}</td>
                        <td className="p-3 text-slate-500">{item.category || "General"}</td>
                        <td className="p-3 text-center font-mono font-black text-blue-600">{item.quantity}</td>
                        <td className="p-3 text-right font-mono font-black text-emerald-600 pr-5">₹{(Number(item.retailPrice || item.unitPrice || item.mrp || 0)).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* --- PREVIEW REPORT MODAL --- */}
      <AnimatePresence>
        {showPreview && (
          <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 font-sans">
            <motion.div
              initial={{ scale: 0.98, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.98, opacity: 0, y: 20 }}
              className="bg-white w-full max-w-6xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-full max-h-[90vh]"
            >
              {/* Header */}
              <div className="bg-slate-900 p-5 text-white flex items-center justify-between shrink-0">
                <div>
                  <h3 className="text-sm font-black uppercase tracking-wider flex items-center gap-2">
                    <FileText size={18} className="text-blue-400" /> Document Preview Engine
                  </h3>
                  <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider font-bold">
                    Context: {isGlobal ? "GLOBAL NETWORK METRICS" : `BRANCH PROFILE: ${viewingStore?.storeName}`} | Scope: {dateFilter.start || dateFilter.end ? `${dateFilter.start || 'Start'} to ${dateFilter.end || 'Now'}` : 'Today'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => executeExport("pdf")} className="px-4 py-2 bg-rose-500 hover:bg-rose-400 text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-colors shadow-sm flex items-center gap-2">
                    <Download size={14}/> Download PDF
                  </button>
                  <div className="w-px h-6 bg-slate-700"></div>
                  <button onClick={() => setShowPreview(false)} className="p-1.5 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-all">
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Preview Content Body */}
              <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-10 custom-scrollbar bg-slate-50">
                
                {/* Section 1: KPI Summary */}
                <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5">1. Core Performance Matrix</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-[11px] uppercase tracking-wider">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                      <span className="text-slate-500 font-bold">Net Yield</span>
                      <strong className="text-emerald-600 font-mono text-sm">₹{currentViewData.kpi.sum.toLocaleString()}</strong>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                      <span className="text-slate-500 font-bold">Pending Logs</span>
                      <strong className="text-amber-600 font-mono text-sm">{currentViewData.kpi.pending}</strong>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                      <span className="text-slate-500 font-bold">Completed Logs</span>
                      <strong className="text-blue-600 font-mono text-sm">{currentViewData.kpi.completed}</strong>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                      <span className="text-slate-500 font-bold">Total CRM Units</span>
                      <strong className="text-purple-600 font-mono text-sm">{currentViewData.kpi.customers}</strong>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                      <span className="text-slate-500 font-bold">Total Stock Qty</span>
                      <strong className="text-slate-800 font-mono text-sm">{currentViewData.kpi.stockQty.toLocaleString()}</strong>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex justify-between items-center">
                      <span className="text-slate-500 font-bold">Stock Valuation</span>
                      <strong className="text-emerald-600 font-mono text-sm">₹{currentViewData.kpi.stockVal.toLocaleString()}</strong>
                    </div>
                  </div>
                </section>

                {/* Section 2: Sales Ledger */}
                <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5">2. Recent Sales Ledger</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-[11px] border-collapse">
                      <thead className="bg-slate-50 text-slate-500 uppercase font-bold tracking-wider">
                        <tr>
                          <th className="p-3 border-b border-slate-200 rounded-tl-lg">Invoice ID</th>
                          {isGlobal && <th className="p-3 border-b border-slate-200">Origin Store</th>}
                          <th className="p-3 border-b border-slate-200">Date Log</th>
                          <th className="p-3 border-b border-slate-200">Customer Reference</th>
                          <th className="p-3 border-b border-slate-200 text-right">Net Payload</th>
                          <th className="p-3 border-b border-slate-200 text-center rounded-tr-lg">State</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700 font-medium">
                        {currentViewData.sales.length === 0 ? (
                          <tr><td colSpan={isGlobal ? 6 : 5} className="p-6 text-center text-slate-400">No Sales Data Found</td></tr>
                        ) : (
                          currentViewData.sales.slice(0, 50).map((s, i) => (
                            <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                              <td className="p-3 font-mono font-bold text-blue-600">{s.invoiceNo || s.id?.slice(-6).toUpperCase() || 'N/A'}</td>
                              {isGlobal && <td className="p-3 font-bold">{s.storeName || 'N/A'}</td>}
                              <td className="p-3">{s.timestamp?.split("T")[0] || '-'}</td>
                              <td className="p-3">{s.customer?.name || 'Walk-In Client'}</td>
                              <td className="p-3 text-right font-mono font-bold">₹{s.financials?.netPayable || 0}</td>
                              <td className="p-3 text-center">
                                <span className={`px-2 py-0.5 rounded text-[9px] uppercase font-bold ${s.orderStatus === 'Completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {s.orderStatus || 'Completed'}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                {/* Section 3: Inventory Ledger */}
                <section className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest border-b border-slate-100 pb-3 mb-5">3. Physical Inventory Master Sheet</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-[11px] border-collapse">
                      <thead className="bg-slate-50 text-slate-500 uppercase font-bold tracking-wider">
                        <tr>
                          <th className="p-3 border-b border-slate-200 rounded-tl-lg">SKU / Code</th>
                          <th className="p-3 border-b border-slate-200">Nomenclature</th>
                          {isGlobal && <th className="p-3 border-b border-slate-200">Origin Store</th>}
                          <th className="p-3 border-b border-slate-200">Classification</th>
                          <th className="p-3 border-b border-slate-200 text-center">Unit Qty</th>
                          <th className="p-3 border-b border-slate-200 text-right">Retail Rate</th>
                          <th className="p-3 border-b border-slate-200 text-right rounded-tr-lg">Asset Total Value</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700 font-medium">
                         {currentViewData.inventory.length === 0 ? (
                           <tr><td colSpan={isGlobal ? 7 : 6} className="p-6 text-center text-slate-400">No Inventory Data Found</td></tr>
                         ) : (
                          currentViewData.inventory.map((inv, i) => {
                            const qty = Number(inv.quantity) || 0;
                            const price = Number(inv.retailPrice || inv.mrp || inv.unitPrice || 0);
                            return (
                              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                <td className="p-3 font-mono font-bold text-slate-500">{inv.productCode || inv.sku || '-'}</td>
                                <td className="p-3 font-bold text-slate-900">{inv.productName || 'Unknown Product'}</td>
                                {isGlobal && <td className="p-3">{inv.storeName || '-'}</td>}
                                <td className="p-3 uppercase text-[9px] text-slate-500">{inv.productType || 'General'}</td>
                                <td className="p-3 text-center font-mono font-black text-blue-600">{qty}</td>
                                <td className="p-3 text-right font-mono">₹{price.toLocaleString()}</td>
                                <td className="p-3 text-right font-mono font-black text-emerald-600">₹{(qty * price).toLocaleString()}</td>
                              </tr>
                            );
                          })
                         )}
                      </tbody>
                    </table>
                  </div>
                </section>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}